import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminSettings, updateAdminSettings, type AdminBranding } from '../../api/admin';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { UploadField } from '../../components/UploadField';
import { uploadAsset } from '../../api/uploads';

/** Platform settings — 6 tabs (General, Email, SMS, Branding, Security, Data retention). */
export function PlatformSettings() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Settings"
        subtitle="Brand, contact info, billing defaults, and data retention."
      />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'general', label: 'General', content: <GeneralTab /> },
            { id: 'email', label: 'Email', content: <EmailTab /> },
            { id: 'sms', label: 'SMS', content: <SmsTab /> },
            { id: 'branding', label: 'Branding', content: <BrandingTab /> },
            { id: 'security', label: 'Security', content: <SecurityTab /> },
            { id: 'retention', label: 'Data retention', content: <RetentionTab /> },
          ]}
        />
      </Card>
    </div>
  );
}

function GeneralTab() {
  const { data: settings } = useAdminSettings();
  const qc = useQueryClient();
  const [brand, setBrand] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [terms, setTerms] = useState('');
  const [privacy, setPrivacy] = useState('');
  const [currency, setCurrency] = useState('BDT');
  const [timezone, setTimezone] = useState('Asia/Dhaka');
  const [allowReseller, setAllowReseller] = useState(true);
  const [allowDirect, setAllowDirect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setBrand(settings.branding.brandName);
    setSupportEmail(settings.branding.supportEmail);
    setSupportPhone(settings.branding.supportPhone ?? '');
    setTerms(settings.termsUrl ?? '');
    setPrivacy(settings.privacyUrl ?? '');
    setCurrency(settings.defaultCurrency);
    setTimezone(settings.defaultTimezone);
    setAllowReseller(settings.allowResellerSignup);
    setAllowDirect(settings.allowDirectSignup);
  }, [settings]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({
        defaultCurrency: currency,
        defaultTimezone: timezone,
        termsUrl: terms.trim() || null,
        privacyUrl: privacy.trim() || null,
        allowResellerSignup: allowReseller,
        allowDirectSignup: allowDirect,
        branding: { brandName: brand.trim(), supportEmail: supportEmail.trim(), supportPhone: supportPhone.trim() || null },
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  const selectCls =
    'block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700';

  if (!settings) {
    return <div className="px-1 py-8 space-y-4" aria-busy="true"><div className="skeleton h-8 w-64 max-w-full rounded" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>;
  }

  return (
    <div className="space-y-5">
      <Input label="Brand name" value={brand} onChange={(e) => setBrand(e.target.value)} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Default currency</label>
          <select className={selectCls} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {settings.activeCurrencies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Default timezone</label>
          <select className={selectCls} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <option value="Asia/Dhaka">Asia/Dhaka</option>
            <option value="UTC">UTC</option>
            <option value="Asia/Kolkata">Asia/Kolkata</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="Support email" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
        <Input label="Support phone" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
      </div>
      <Input label="Terms URL" type="url" value={terms} onChange={(e) => setTerms(e.target.value)} />
      <Input label="Privacy URL" type="url" value={privacy} onChange={(e) => setPrivacy(e.target.value)} />

      <div className="space-y-2 pt-3 border-t border-zinc-200 dark:border-zinc-700">
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={allowReseller}
            onChange={(e) => setAllowReseller(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Allow public reseller application form
        </label>
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={allowDirect}
            onChange={(e) => setAllowDirect(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Allow direct (no-reseller) signups
        </label>
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function EmailTab() {
  const { data: settings } = useAdminSettings();
  const qc = useQueryClient();
  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    const e = settings.email;
    setFromName(e.fromName);
    setFromAddress(e.fromAddress);
    setReplyTo(e.replyTo ?? '');
    setHost(e.smtp.host ?? '');
    setPort(String(e.smtp.port));
    setSecure(e.smtp.secure);
    setUsername(e.smtp.username ?? '');
  }, [settings]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({
        email: {
          fromName: fromName.trim(),
          fromAddress: fromAddress.trim(),
          replyTo: replyTo.trim() || null,
          smtp: {
            host: host.trim() || null,
            port: Number(port) || 587,
            secure,
            username: username.trim() || null,
            // Only send the password when the admin typed a new one (write-only; sealed server-side).
            ...(password ? { password } : {}),
          },
        },
      });
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return <div className="px-1 py-8 space-y-4" aria-busy="true"><div className="skeleton h-8 w-64 max-w-full rounded" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>;
  }
  const passwordSet = settings.email.smtp.passwordSet;

  return (
    <div className="space-y-5">
      <Banner tone="info">SMTP credentials are encrypted at rest and never displayed.</Banner>

      <Input label="From name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
      <Input label="From address" type="email" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} />
      <Input label="Reply-to address" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">SMTP transport</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" />
          <Input label="Port" value={port} onChange={(e) => setPort(e.target.value)} />
          <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={passwordSet ? '•••••••• (set — type to replace)' : 'Not set'}
          />
        </div>
        <label className="mt-3 flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={secure}
            onChange={(e) => setSecure(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Use implicit TLS (port 465)
        </label>
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-between items-center gap-2">
        <Button variant="secondary" disabled title="Test send coming soon">Send test email</Button>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
          <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
        </div>
      </div>
    </div>
  );
}

function SmsTab() {
  return (
    <div className="space-y-5">
      <Banner tone="info">SMS uses a single national gateway. Falls back to email if delivery fails.</Banner>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Provider</label>
        <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
          <option>SSL Wireless</option>
          <option>BulkSMSBD</option>
          <option>Twilio (international)</option>
        </select>
      </div>
      <Input label="Sender ID" defaultValue="POSSIGNIN" />
      <Input label="API key" type="password" placeholder="••••••••" />
      <Input label="Default originating number" defaultValue="+8801717540035" />

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-between gap-2">
        <Button variant="secondary">Send test SMS</Button>
        <Button>Save changes</Button>
      </div>
    </div>
  );
}

/** Platform-brand defaults (the brand every reseller white-labels over). Wired to
 *  GET/PATCH /admin/settings.branding + POST /uploads/asset (#54). */
function BrandingTab() {
  const qc = useQueryClient();
  const { data: settings } = useAdminSettings();
  const [draft, setDraft] = useState<AdminBranding | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setDraft(settings.branding);
  }, [settings]);

  if (!draft) {
    return <div className="py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">Loading branding…</div>;
  }

  const set = <K extends keyof AdminBranding>(key: K, value: AdminBranding[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  async function save() {
    if (!draft) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await updateAdminSettings({ branding: draft });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch {
      setError('Could not save branding. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Input label="Brand name" value={draft.brandName} onChange={(e) => set('brandName', e.target.value)} />

      <div className="grid sm:grid-cols-3 gap-4">
        <UploadField label="Logo (light)" hint="PNG/SVG · ≤ 2 MB" value={draft.logoLightUrl} onUpload={async (f) => set('logoLightUrl', (await uploadAsset(f)).url)} />
        <UploadField label="Logo (dark)" hint="PNG/SVG · ≤ 2 MB" value={draft.logoDarkUrl} onUpload={async (f) => set('logoDarkUrl', (await uploadAsset(f)).url)} />
        <UploadField label="Favicon" hint="ICO/PNG · ≤ 2 MB" value={draft.faviconUrl} onUpload={async (f) => set('faviconUrl', (await uploadAsset(f)).url)} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <ColorRow label="Primary" hex={draft.primaryColor} onChange={(v) => set('primaryColor', v)} />
        <ColorRow label="Brand accent" hex={draft.accentColor} onChange={(v) => set('accentColor', v)} />
        <ColorRow label="Surface" hex={draft.surfaceColor} onChange={(v) => set('surfaceColor', v)} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="Support email" value={draft.supportEmail} onChange={(e) => set('supportEmail', e.target.value)} />
        <Input label="Support phone" value={draft.supportPhone ?? ''} onChange={(e) => set('supportPhone', e.target.value || null)} />
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">{error}</span>}
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function ColorRow({ label, hex, onChange }: { label: string; hex: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">{label}</label>
      <div className="flex items-center gap-2 p-2 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-zinc-200 dark:border-zinc-700 bg-transparent cursor-pointer"
          aria-label={`${label} color`}
        />
        <input value={hex} onChange={(e) => onChange(e.target.value)} className="flex-1 mono text-xs bg-transparent focus:outline-none" />
      </div>
    </div>
  );
}

function SecurityTab() {
  const qc = useQueryClient();
  const { data: settings } = useAdminSettings();
  const [maxSessions, setMaxSessions] = useState('5');
  const [otpRequired, setOtpRequired] = useState(true);
  const [otpDelivery, setOtpDelivery] = useState<'sms' | 'email' | 'both'>('both');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setMaxSessions(String(settings.maxActiveSessionsDefault));
    setOtpRequired(settings.signupOtpRequired);
  }, [settings]);

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({ maxActiveSessionsDefault: Number(maxSessions), signupOtpRequired: otpRequired });
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Session lifetime</label>
        <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
          <option>30 minutes</option>
          <option>2 hours</option>
          <option selected>8 hours</option>
          <option>7 days (with "stay signed in")</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
          Maximum simultaneous device sessions per user
        </label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={1}
            max={20}
            value={maxSessions}
            onChange={(e) => setMaxSessions(e.target.value)}
            className="w-28"
          />
          <span className="text-sm text-zinc-500 dark:text-zinc-400">devices (range 1–20)</span>
        </div>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          When a user signs in on more devices than the cap, the oldest session is automatically
          revoked. Per-user override available from <span className="mono text-[11px]">/admin/users/&lt;id&gt;</span> for VIP / shared accounts.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Signup OTP</label>
        <div className="space-y-2">
          <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={otpRequired}
              onChange={(e) => setOtpRequired(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            Require 4-digit OTP at signup (verifies email + phone)
          </label>
          {otpRequired && (
            <div className="ml-7 space-y-2">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Delivery channel</div>
              <div className="flex gap-3">
                {(['sms', 'email', 'both'] as const).map((d) => (
                  <label key={d} className="flex items-center gap-1.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                    <input
                      type="radio"
                      name="otp-delivery"
                      checked={otpDelivery === d}
                      onChange={() => setOtpDelivery(d)}
                      className="text-brand focus:ring-brand"
                    />
                    <span className="capitalize">{d === 'both' ? 'SMS + Email' : d}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Two-factor auth</label>
        <div className="space-y-2">
          <Check label="Required for platform admins (always on)" defaultChecked />
          <Check label="Required for reseller admins" defaultChecked />
          <Check label="Required for business admins" />
          <Check label="Allow recovery codes" defaultChecked />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Password policy</label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Minimum length" defaultValue="12" />
          <Input label="Max age (days)" defaultValue="180" />
        </div>
        <div className="mt-3 space-y-2">
          <Check label="Require uppercase + lowercase" defaultChecked />
          <Check label="Require number" defaultChecked />
          <Check label="Require symbol" defaultChecked />
          <Check label="Block common-leak passwords (HIBP)" defaultChecked />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">IP allowlist (admin only)</label>
        <textarea
          rows={3}
          placeholder="One IP/CIDR per line"
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 mono text-xs"
          defaultValue="202.74.0.0/16"
        />
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function RetentionTab() {
  return (
    <div className="space-y-5">
      <Banner tone="warning">
        Retention changes apply going forward only. They never retroactively delete data.
      </Banner>

      <RetentionRow label="Audit log" defaultValue="365" />
      <RetentionRow label="Webhook deliveries" defaultValue="30" />
      <RetentionRow label="Notifications" defaultValue="180" />
      <RetentionRow label="Soft-deleted records" defaultValue="30" hint="Days before hard-delete" />
      <RetentionRow label="Canceled subscription data" defaultValue="30" hint="Days before erasure" />

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end">
        <Button>Save changes</Button>
      </div>
    </div>
  );
}

function RetentionRow({ label, defaultValue, hint }: { label: string; defaultValue: string; hint?: string }) {
  return (
    <div className="grid sm:grid-cols-[1fr_120px_auto] gap-3 items-center">
      <div>
        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{label}</div>
        {hint && <div className="text-xs text-zinc-500 dark:text-zinc-400">{hint}</div>}
      </div>
      <input
        defaultValue={defaultValue}
        className="px-3 py-2 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 tabular-nums"
      />
      <span className="text-xs text-zinc-500 dark:text-zinc-400">days</span>
    </div>
  );
}

function Check({ label, defaultChecked }: { label: string; defaultChecked?: boolean }) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="checkbox" defaultChecked={defaultChecked} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
